// Sidebar — the app's left menu (pen "Left menu – detail design", component
// "Sidebar · v4", decided 2026-10-20; collapsible per pen I-11..I-14), pure.
//
// Shell chrome filled by registrations (sidebarEntries.ts): it knows no views
// and imports no module. Top to bottom: the school switcher; the CTA ("New
// project"); the search field that opens the ⌘K
// omnibar; the registered menu entries; the registered recent items, one
// labelled section per registered list (today: projects), in pen's list
// pattern (SidebarRecent.tsx; hidden when empty, and in the rail: projects
// appear only in the peek, owner 2026-10-22); then the foot: the connection indicator (only while
// something is wrong), registered foot rows, the account, the fold row and
// the version. Entries that don't work yet are not registered as working, so
// they are hidden, never disabled.
//
// Three layouts (`SidebarLayout`): expanded; rail (folded to an icon column);
// peek (the expanded menu laid over the content while the rail is hovered,
// I-13). Folding only hides labels: every row keeps its height and every
// icon its place on the x = 26 axis (I-14), so nothing jumps under the
// pointer.
//
// Pure/props-driven: every intent reports up. The one local behaviour is
// arrow-key movement inside the menu entries (view-ephemeral focus).

export type { SidebarLayout };
export { Sidebar, SidebarItem };

import { exhaustSwitchCase } from "@bilda/utils";
import { useLingui } from "@lingui/react/macro";
import type { KeyboardEvent, ReactNode } from "react";
import { Icon, type IconName } from "../Icon/Icon.js";
import { Kbd, type KbdPlatform } from "../Kbd/Kbd.js";
import { Tooltip } from "../Tooltip/Tooltip.js";
import { type RecentListProps, SidebarRecent } from "./SidebarRecent.js";
import { SidebarSchool } from "./SidebarSchool.js";
import {
    countLabel,
    iconOf,
    type SchoolSwitcher,
    type SidebarEntry,
    sidebarSectionsOf,
} from "./sidebarEntries.js";
import "./Sidebar.css";

// One row: icon, then its label while expanded. The tooltip names it when
// the label is hidden (folded) or truncated.
const SidebarItem = ({
    label,
    icon,
    current,
    expanded,
    count = "",
    ariaExpanded,
    onSelect,
}: {
    readonly label: string;
    readonly icon: IconName;
    readonly current: boolean;
    readonly expanded: boolean;
    // A count badge ("3", "9+"); "" shows none.
    readonly count?: string;
    // Set on a row that opens or folds something (the fold row).
    readonly ariaExpanded?: boolean;
    readonly onSelect: () => void;
}): ReactNode => (
    <Tooltip content={label} side="right">
        <button
            type="button"
            className="bilda-sidebar__item"
            aria-current={current ? "page" : undefined}
            aria-label={expanded ? undefined : label}
            aria-expanded={ariaExpanded}
            data-active={current ? "true" : undefined}
            onClick={onSelect}
        >
            <span className="bilda-sidebar__item-icon" aria-hidden="true">
                <Icon name={icon} />
            </span>
            {expanded ? (
                <span className="bilda-sidebar__item-label">{label}</span>
            ) : null}
            {expanded && count !== "" ? (
                <span className="bilda-sidebar__count">{count}</span>
            ) : null}
        </button>
    </Tooltip>
);

// Arrow keys move between the menu entries (one list, spec §5).
const moveFocus = (e: KeyboardEvent<HTMLDivElement>): void => {
    const step = e.key === "ArrowDown" ? 1 : e.key === "ArrowUp" ? -1 : 0;
    if (step === 0) return;
    const rows = [...e.currentTarget.querySelectorAll("button")];
    const at = rows.findIndex((row) => row.isSameNode(document.activeElement));
    const next = rows[(at + step + rows.length) % rows.length];
    if (at === -1 || next === undefined) return;
    e.preventDefault();
    next.focus();
};

type SidebarLayout = "expanded" | "rail" | "peek";

type SidebarProps = {
    readonly layout: SidebarLayout;
    // Fold (expanded) or unfold (rail, and "Keep menu open" in the peek).
    readonly onToggle: () => void;
    readonly school: SchoolSwitcher;
    // Set up a new project (and open it).
    readonly onNewProject: () => void;
    readonly onOpenSearch: () => void;
    // Which register the ⌘K hint renders in (the host resolves it).
    readonly platform: KbdPlatform;
    // What modules registered: menu entries, recent items, foot rows.
    readonly entries: readonly SidebarEntry[];
    // The recent lists, in order: how each one's items are listed (pins,
    // sort, density) and named.
    readonly recentLists: readonly RecentListProps[];
    // The connection indicator (renders nothing while online).
    readonly connection: ReactNode;
    readonly account: ReactNode;
    readonly version: ReactNode;
};

// The fold row's label + glyph per layout (pen I-11..I-13).
const useToggle = (
    layout: SidebarLayout,
): { readonly label: string; readonly icon: IconName } => {
    const { t } = useLingui();
    switch (layout) {
        case "expanded":
            return { label: t`Collapse menu`, icon: "panel-left-close" };
        case "rail":
            return { label: t`Expand menu`, icon: "panel-left-open" };
        case "peek":
            return { label: t`Keep menu open`, icon: "pin" };
        default:
            return exhaustSwitchCase(layout);
    }
};

const Sidebar = ({
    layout,
    onToggle,
    school,
    onNewProject,
    onOpenSearch,
    platform,
    entries,
    recentLists,
    connection,
    account,
    version,
}: SidebarProps): ReactNode => {
    const { t } = useLingui();
    const labelled = layout !== "rail";
    const toggle = useToggle(layout);
    const sections = sidebarSectionsOf(entries);
    const newLabel = t`New project`;
    const searchName =
        platform === "mac" ? t`Search, Command K` : t`Search, Control K`;
    return (
        <nav
            aria-label={t`Main menu`}
            className="bilda-sidebar"
            data-layout={layout}
            // A passive anchor for the guided tour's spotlight measurement
            // (the peek is a transient copy, not the anchor).
            data-tour-target={layout === "peek" ? undefined : "rail"}
        >
            <SidebarSchool school={school} expanded={labelled} />

            <Tooltip content={newLabel} side="right">
                <button
                    type="button"
                    className="bilda-sidebar__cta"
                    aria-label={labelled ? undefined : newLabel}
                    onClick={onNewProject}
                >
                    <span
                        className="bilda-sidebar__item-icon"
                        aria-hidden="true"
                    >
                        <Icon name="plus" />
                    </span>
                    {labelled ? (
                        <span className="bilda-sidebar__item-label">
                            {newLabel}
                        </span>
                    ) : null}
                </button>
            </Tooltip>

            <Tooltip content={searchName} side="right">
                <button
                    type="button"
                    className="bilda-sidebar__search"
                    aria-label={searchName}
                    aria-haspopup="dialog"
                    onClick={onOpenSearch}
                >
                    <span
                        className="bilda-sidebar__item-icon"
                        aria-hidden="true"
                    >
                        <Icon name="search" />
                    </span>
                    {labelled ? (
                        <>
                            <span className="bilda-sidebar__item-label">
                                {t`Search anything`}
                            </span>
                            <Kbd
                                // One cap ("⌘K"), as the design draws it.
                                keys={[platform === "mac" ? "⌘K" : "Ctrl K"]}
                                platform={platform}
                                className="bilda-sidebar__kbd"
                                aria-hidden="true"
                            />
                        </>
                    ) : null}
                </button>
            </Tooltip>

            {/* biome-ignore lint/a11y/noStaticElementInteractions: arrow keys move focus between the buttons inside */}
            <div className="bilda-sidebar__menu" onKeyDown={moveFocus}>
                {sections.menu.map((entry) => (
                    <SidebarItem
                        key={entry.id}
                        label={entry.label}
                        icon={iconOf(entry.icon)}
                        current={entry.current}
                        expanded={labelled}
                        onSelect={entry.open}
                    />
                ))}
            </div>

            <div className="bilda-sidebar__recent">
                {labelled
                    ? recentLists.map((list) => (
                          <SidebarRecent
                              key={list.id}
                              items={sections.recent.filter(
                                  (item) => item.list === list.id,
                              )}
                              list={list}
                          />
                      ))
                    : null}
            </div>

            <div className="bilda-sidebar__footer">
                {connection}
                {sections.foot.map((row) => (
                    <SidebarItem
                        key={row.id}
                        label={row.label}
                        icon={iconOf(row.icon)}
                        current={false}
                        expanded={labelled}
                        count={countLabel(row.count)}
                        onSelect={row.open}
                    />
                ))}
                {account}
                <SidebarItem
                    label={toggle.label}
                    icon={toggle.icon}
                    current={false}
                    expanded={labelled}
                    ariaExpanded={layout === "expanded"}
                    onSelect={onToggle}
                />
                <div className="bilda-sidebar__meta">{version}</div>
            </div>
        </nav>
    );
};
